import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuChartColumn, LuCircleCheck, LuCircleX, LuFolderKanban } from 'react-icons/lu';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';
import { getFilterValueLabel } from './projectColumns';

const UNSPECIFIED = '__unspecified__';

const Bar = ({ share, className }) => (
  <span className="tw-block tw-h-2 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10">
    <span className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-700 tw-ease-out ${className}`} style={{ width: `${share}%` }} />
  </span>
);

Bar.propTypes = { share: PropTypes.number.isRequired, className: PropTypes.string.isRequired };

/** "İstatistikler": project counts by status and by phase. */
const ProjectStatsModal = ({ projects, onClose }) => {
  const { t, language, locale } = useTranslation();

  const stats = useMemo(() => {
    const phaseStats = {};
    const statusStats = { Aktif: 0, Pasif: 0 };
    projects.forEach((project) => {
      const phase = project.projectPhase || UNSPECIFIED;
      phaseStats[phase] = (phaseStats[phase] || 0) + 1;
      const status = project.projectStatus || 'Aktif';
      if (status === 'Aktif' || status === 'Pasif') statusStats[status] += 1;
    });
    return { phaseStats, statusStats };
  }, [projects]);

  const total = projects.length;
  const percent = (count) => (total > 0 ? (count / total) * 100 : 0);
  const formatPercent = (count) => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(percent(count) / 100);
  const phaseLabel = (phase) => (phase === UNSPECIFIED ? t('projectManagement.statsModal.unspecified') : getFilterValueLabel(t, 'projectPhase', phase));

  const summary = [
    { key: 'total', label: t('projectManagement.statsModal.total'), value: total, icon: LuFolderKanban, tone: 'tw-text-slate-900 dark:tw-text-white', ring: 'tw-border-slate-200 tw-bg-slate-50 dark:tw-border-white/10 dark:tw-bg-white/[0.03]' },
    { key: 'active', label: t('projectManagement.statsModal.active'), value: stats.statusStats.Aktif, share: formatPercent(stats.statusStats.Aktif), icon: LuCircleCheck, tone: 'tw-text-emerald-700 dark:tw-text-emerald-300', ring: 'tw-border-emerald-500/20 tw-bg-emerald-500/[0.07] dark:tw-border-emerald-400/20' },
    { key: 'inactive', label: t('projectManagement.statsModal.inactive'), value: stats.statusStats.Pasif, share: formatPercent(stats.statusStats.Pasif), icon: LuCircleX, tone: 'tw-text-slate-700 dark:tw-text-slate-200', ring: 'tw-border-slate-300/60 tw-bg-slate-500/[0.07] dark:tw-border-white/10' },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={t('projectManagement.statsModal.title')}
      subtitle={t('projectManagement.statsModal.subtitle', { count: total.toLocaleString(locale) })}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30" size="xs">
          <LuChartColumn aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
    >
      <div className="tw-space-y-6">
        <dl className="tw-grid tw-grid-cols-3 tw-gap-2.5">
          {summary.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.key} className={`tw-rounded-2xl tw-border tw-px-3 tw-py-3 sm:tw-px-4 ${item.ring}`}>
                <dt className="tw-flex tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">
                  <Icon aria-hidden="true" className={`tw-h-3.5 tw-w-3.5 tw-shrink-0 ${item.tone}`} />
                  <span className="tw-truncate">{item.label}</span>
                </dt>
                <dd className={`tw-mt-1 tw-text-2xl tw-font-semibold tw-tabular-nums ${item.tone}`}>
                  {item.value.toLocaleString(locale)}
                  {item.share && <span className="tw-ml-1.5 tw-text-xs tw-font-semibold tw-text-slate-400 dark:tw-text-slate-500">{item.share}</span>}
                </dd>
              </div>
            );
          })}
        </dl>

        <section className="tw-space-y-3">
          <h3 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{t('projectManagement.statsModal.statusTitle')}</h3>
          {[
            { key: 'Aktif', label: t('projectManagement.status.active'), bar: 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500' },
            { key: 'Pasif', label: t('projectManagement.status.inactive'), bar: 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500' },
          ].map((row) => (
            <div key={row.key} className="tw-space-y-1.5">
              <div className="tw-flex tw-items-center tw-justify-between tw-text-sm">
                <span className="tw-font-medium tw-text-slate-700 dark:tw-text-slate-200">{row.label}</span>
                <span className="tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
                  {stats.statusStats[row.key].toLocaleString(locale)} · {formatPercent(stats.statusStats[row.key])}
                </span>
              </div>
              <Bar share={percent(stats.statusStats[row.key])} className={row.bar} />
            </div>
          ))}
        </section>

        <section className="tw-space-y-3">
          <h3 className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{t('projectManagement.statsModal.phaseTitle')}</h3>
          <div className="tw-grid tw-gap-2.5 sm:tw-grid-cols-2">
            {Object.entries(stats.phaseStats)
              .sort((a, b) => b[1] - a[1])
              .map(([phase, count]) => (
                <div key={phase} className="tw-space-y-2 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-4 tw-py-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                  <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
                    <span className="tw-min-w-0 tw-text-sm tw-font-medium tw-leading-snug tw-text-slate-700 dark:tw-text-slate-200" title={phase === UNSPECIFIED ? undefined : phase}>
                      {phaseLabel(phase)}
                    </span>
                    <span className="tw-shrink-0 tw-text-lg tw-font-semibold tw-text-slate-900 tw-tabular-nums dark:tw-text-white">{count.toLocaleString(locale)}</span>
                  </div>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-flex-1"><Bar share={percent(count)} className="tw-bg-gradient-to-r tw-from-indigo-400 tw-to-violet-500" /></span>
                    <span className="tw-w-12 tw-text-right tw-text-xs tw-font-semibold tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">{formatPercent(count)}</span>
                  </div>
                </div>
              ))}
          </div>
        </section>
      </div>
    </Modal>
  );
};

ProjectStatsModal.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ProjectStatsModal;
